import { GridColumnProps } from '@progress/kendo-react-grid';
import { GridColumnConfig } from '../../models/gridCoumnConfig';
import { Fragment } from 'react';
import { nanoid } from 'nanoid';
import { IconButton, Tooltip } from '@mui/material';
import Clear from '@mui/icons-material/Clear';
import dayjs from 'dayjs';
import { State } from '@progress/kendo-data-query';
import { parseErrorMessageRecursive } from '../../utils/yup-error-parser.util';
// import { useSnackbar } from '../../hooks/use-snackbar.hook';

export const reorderColumns = (columns: GridColumnConfig[], sortedColumns: GridColumnProps[]): GridColumnConfig[] => {
  const sortedFields = sortedColumns.map((col) => col.field);

  // First, reorder columns that exist in sortedColumns based on the sortedFields order
  const reordered = sortedFields
    .map((field) => {
      const existingCol = columns.find((col) => col.field === field);
      if (existingCol) {
        return { ...existingCol, hide: false }; // ensure hide: false
      }
      return null; // for safety, in case field is not found
    })
    .filter(Boolean) as GridColumnConfig[];

  // Then, add the remaining columns that are not in sortedColumns
  const remainingColumns = columns.filter((col) => !sortedFields.includes(col.field));

  return [...reordered, ...remainingColumns];
};

// export const reorderColumns = (columns: GridColumnConfig[], sortedColumns: GridColumnProps[]): GridColumnConfig[] => {
//   const sortedFields = sortedColumns.map((col) => col.field).filter((field): field is string => !!field);

//   const usedFields = new Set<string>();
//   const usedParentFields = new Set<string>();

//   const matchColumn = (field: string): GridColumnConfig | null => {
//     for (const col of columns) {
//       // Match flat column
//       if (col.field === field) {
//         usedFields.add(col.field);
//         return { ...col, hide: false };
//       }

//       // Match child column in grouped columns
//       if (Array.isArray(col.children)) {
//         const matchingChild = col.children.find((child) => child.field === field);
//         if (matchingChild) {
//           usedFields.add(field);

//           const key = JSON.stringify(col.children.map((c) => c.field).sort());
//           if (usedParentFields.has(key)) return null;
//           usedParentFields.add(key);

//           const updatedChildren = col.children.map((child) => ({
//             ...child,
//             hide: sortedFields.includes(child.field) ? false : child.hide,
//           }));

//           return {
//             ...col,
//             children: updatedChildren,
//             hide: false,
//           };
//         }
//       }
//     }
//     return null;
//   };

//   const reorderedByField = sortedFields.map((field) => matchColumn(field)).filter(Boolean) as GridColumnConfig[];

//   const reorderedFieldSet = new Set(
//     reorderedByField.flatMap((col) => {
//       if (Array.isArray(col.children)) return col.children.map((c) => c.field);
//       return col.field ? [col.field] : [];
//     })
//   );

//   const finalOrdered: GridColumnConfig[] = [];

//   for (const col of columns) {
//     // Already handled in reordered
//     if (Array.isArray(col.children)) {
//       const childFields = col.children.map((c) => c.field);
//       if (childFields.every((f) => reorderedFieldSet.has(f))) {
//         const fromReordered = reorderedByField.find(
//           (c) => JSON.stringify(c.children?.map((cc) => cc.field).sort()) === JSON.stringify(childFields.sort())
//         );
//         if (fromReordered) finalOrdered.push(fromReordered);
//         continue;
//       }
//     } else if (col.field && reorderedFieldSet.has(col.field)) {
//       const fromReordered = reorderedByField.find((c) => c.field === col.field);
//       if (fromReordered) finalOrdered.push(fromReordered);
//       continue;
//     }

//     // Not in reorder list, keep original
//     finalOrdered.push(col);
//   }

//   return finalOrdered;
// };

export const KENDO_FILTERS = [
  { operator: 'eq', value: 'equal to' },
  { operator: 'neq', value: 'not equal to' },
  { operator: 'isnull', value: 'is equal to null' },
  { operator: 'isnotnull', value: 'is not equal to null' },
  { operator: 'lt', value: 'less than' },
  { operator: 'lte', value: 'less than or equal to' },
  { operator: 'gt', value: 'greater than' },
  { operator: 'gte', value: 'greater than or equal to' },
  { operator: 'startswith', value: 'starts with' },
  { operator: 'endswith', value: 'ends with' },
  { operator: 'contains', value: 'contains' },
  { operator: 'doesnotcontain', value: 'does not contain' },
  { operator: 'isempty', value: 'is empty' },
  { operator: 'isnotempty', value: 'is not empty' },
];

export function validateDate(data: any) {
  return typeof data === 'string' ? false : dayjs(data).isValid();
}

interface Filter {
  field: string;
  operator: string;
  value: string | number | Date;
  logic?: string;
}

interface FilterData {
  filters: Filter[];
  logic?: string;
}

interface DisplayColumn {
  field: string;
  title: string;
}

interface RenderGridFilterLabelProps {
  state: any;
  displayColumns: DisplayColumn[];
  closeDefaultFilterFromApi: (filter: Filter) => void;
}

export const RenderGridFilterLabel: React.FC<RenderGridFilterLabelProps> = ({
  state,
  displayColumns,
  closeDefaultFilterFromApi,
}) => {
  const gridFilters = state.filter;

  if (!gridFilters?.filters?.length) {
    return null;
  }
  // console.log(2, gridFilters);
  return (
    <>
      {gridFilters.filters.map((filterData: any) => {
        if (!filterData?.filters) {
          return null;
        }
        const firstFilter = filterData?.filters[0];
        const column = displayColumns.find((field) => field.field === firstFilter.field);

        if (!column) {
          return null;
        }

        let previousOperator = '';

        return (
          <Fragment key={nanoid(32)}>
            <span>
              {column.title + ' '}
              {filterData.filters
                .map((filter: any, i: any) => {
                  let str = '';

                  if (previousOperator !== filter.operator) {
                    str +=
                      KENDO_FILTERS.find((gridOperator) => gridOperator.operator === filter.operator)?.value?.concat(
                        ' '
                      ) || '';
                  }

                  previousOperator = filter.operator;

                  str += filter.value
                    ? validateDate(filter.value)
                      ? dayjs(filter.value).format('MM-DD-YYYY').concat(' ')
                      : filter.value.toString().concat(' ')
                    : '';

                  if (filterData.filters.length > 1 && i < 1) {
                    str += filterData.logic || '';
                  }
                  if (filterData.filters.length > 2 && i > 0) {
                    str += ',';
                  }

                  return str;
                })
                .join(' ')}
            </span>
            <IconButton color="primary" onClick={() => closeDefaultFilterFromApi(firstFilter)}>
              <Tooltip title="Clear">
                <Clear />
              </Tooltip>
            </IconButton>
          </Fragment>
        );
      })}
    </>
  );
};

export const removeFilterFromState = (dataState: State, item: Filter): State => {
  // Create a deep copy of the dataState to avoid mutating the original state
  const copiedDataState = JSON.parse(JSON.stringify(dataState));
  // Filter out the item from the filters array
  const filters =
    copiedDataState?.filter?.filters?.filter((filterData: any) => {
      // Compare the filter's field (or another unique identifier) to the item's field
      if (!filterData?.filters) {
        return true;
      }
      return filterData.filters[0].field !== item.field;
    }) || [];

  // Return the updated state
  return {
    ...copiedDataState,
    filter: {
      ...copiedDataState.filter,
      filters,
    },
  };
};
